/* Which paper a recipe is read on: the exercise book, or the recipe tin.
 *
 * The same shape as lib/hand.js, and deliberately so — one attribute on
 * <html>, `data-skin="tin"`, because the tokens live on :root and every screen
 * reads them from there. Nothing is passed down, no page has to know, and the
 * whole app changes in the same frame.
 *
 * IT IS A SETTING, NOT A PER-RECIPE SWITCH. A recipe that looked different
 * depending on which screen you opened it from would read as a bug, and "her
 * recipe" is not a thing that has two appearances. It is about how YOU like to
 * read, which is exactly what the handwriting toggle beside it is about too.
 *
 * Persisted through the chassis `prefs` proxy, not the recipe store, for the
 * same reason the hand is: changing how you read must never look like an edit
 * to what she wrote.
 *
 * The book is the default because it is the one with the margin, and the
 * margin is where the notes live — which is the whole app.
 */
import { ref } from 'vue';
import { prefs } from '../core/storage.js';

const KEY = 'skin';

/** The two, in the order Settings shows them. */
export const SKINS = [
  { id: 'book', label: 'Exercise book', hint: 'Ruled paper with a red margin' },
  { id: 'tin', label: 'Recipe tin', hint: 'A filed card, no ruling' },
];

const known = (v) => SKINS.some((s) => s.id === v);

/** The current skin: 'book' or 'tin'. Read it, or bind a picker to it. */
export const skin = ref(known(prefs[KEY]) ? prefs[KEY] : 'book');

function paint(id) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  /* Only the non-default is stamped. An app with no attribute is the book,
     which keeps the default working if this module ever fails to run. */
  if (id === 'tin') root.setAttribute('data-skin', 'tin');
  else root.removeAttribute('data-skin');
}

/** Choose one, remember it, repaint. An unknown id is ignored, not stored. */
export function setSkin(id) {
  if (!known(id)) return;
  skin.value = id;
  prefs[KEY] = id;
  paint(id);
}

/* Called once at boot, before first paint, so someone who chose the tin last
   week does not watch the book render and then swap under them. */
export function applySkin() {
  paint(skin.value);
}

applySkin();
